import { Injectable } from "@angular/core";
import moment from "moment";
import { MediaRepository } from "app/js/src/domain/media-repository";
import { GraphicsRepository } from "app/js/src/domain/graphics-repository";
import { MediaItem } from "app/js/src/domain/media";
import { GraphicsInfo } from "app/js/src/domain/graphics-info";
import {
  ClippingStudioMamMediaPage,
  IClippingStudioMamCatalogService,
} from "app/js/src/domain/clipping-studio/i-clipping-studio-mam-catalog.service";
import { ClippingStudioMamItem } from "app/js/src/domain/clipping-studio/clipping-studio-mam-item";
import { isVideoMediaItem } from "app/js/src/domain/mam-explorer";

/** Matches MediaRepository.getMedia hardcoded Limit. */
const API_PAGE_SIZE = 20;

function formatDurationMs(ms: number): string {
  if (ms == null || !Number.isFinite(ms)) return "00:00:00";
  return moment.utc(ms).format("HH:mm:ss");
}

function formatDurationMoment(duration: moment.Duration | undefined): string {
  if (!duration) return "00:00:00";
  return moment.utc(duration.asMilliseconds()).format("HH:mm:ss");
}

function mapMediaItemToMamItem(
  item: MediaItem,
  kind: "image" | "video",
): ClippingStudioMamItem {
  const durationFormatted =
    kind === "video"
      ? formatDurationMoment(item.contentDuration)
      : undefined;
  const info = item as MediaItem & { tags?: { id?: number; name: string }[] };
  const tags = info.tags?.map((t) => ({ id: (t as any).id, name: (t as any).name }));
  return {
    id: `media-${item.id}`,
    title: item.name || item.userFilename || "",
    thumbnailUrl: item.contentSnapshotUrl ?? null,
    durationFormatted,
    durationMs: kind === "video" ? (item.contentMediaDurationMs ?? 0) : undefined,
    kind,
    mamMediaId: item.id,
    ...(tags?.length && { tags }),
    ...(item.uploadedTimeMs != null && { createdAt: item.uploadedTimeMs }),
  };
}

function mapGraphicsInfoToMamItem(g: GraphicsInfo): ClippingStudioMamItem {
  // Show duration only when API provides non-zero value (GFX list returns "00:00:00" / 0)
  const durationFormatted =
    g.durationMS != null && g.durationMS > 0
      ? formatDurationMs(g.durationMS)
      : undefined;
  return {
    id: `gfx-${g.id}`,
    title: g.name || "",
    thumbnailUrl: g.thumbnail || null,
    durationFormatted,
    kind: "gfx",
    mamGraphicsId: g.id,
    isTemplate: g.isTemplate,
    instanceTemplateId: g.instanceTemplateId,
  };
}

/** Remove duplicate MAM items by id (e.g. same GFX in Templates and Graphics). */
function deduplicateMamItemsById(
  items: ClippingStudioMamItem[],
): ClippingStudioMamItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

/**
 * Fetch media list (same params as Scheduler videoLibrary: orderBy 0, sortBy "Name").
 * Pass tagIds/languageCodes to filter on server; empty or undefined = no filter.
 */
async function fetchMediaPage(
  mediaRepository: MediaRepository,
  searchQuery: string,
  offset: number,
  tagIds: number[] = [],
  languageCodes: string[] = [],
): Promise<MediaItem[]> {
  try {
    return await mediaRepository.getMedia(
      searchQuery,
      offset,
      0, // orderByMedia — same as Scheduler
      "Name", // sortByMedia — same as Scheduler
      null as any,
      null as any,
      null as any,
      null as any,
      "All",
      [], // countries
      tagIds,
      [], // assetTypeIds
      languageCodes,
    );
  } catch {
    return [];
  }
}

@Injectable()
export class ClippingStudioMamCatalogService extends IClippingStudioMamCatalogService {
  constructor(
    private mediaRepository: MediaRepository,
    private graphicsRepository: GraphicsRepository,
  ) {
    super();
  }

  /**
   * Returns MAM items for Graphics panel: static images first, then GFX.
   * Loads both sources (media images + Graphic API) and returns only when all are loaded.
   */
  async getMamItemsForGraphics(
    searchQuery: string,
  ): Promise<ClippingStudioMamItem[]> {
    const q = searchQuery.trim().toLowerCase();

    const graphics = await this.graphicsRepository
      .getAll()
      .catch(() => [] as GraphicsInfo[]);
    let result: ClippingStudioMamItem[] = graphics.map(mapGraphicsInfoToMamItem);

    if (q) {
      result = result.filter((item) => item.title.toLowerCase().includes(q));
    }
    return deduplicateMamItemsById(result);
  }

  /**
   * Fill-loop: keep requesting GetMediaList pages until enough videos are
   * collected (or the API cursor is exhausted). Same pattern as MAM Explorer.
   */
  async getMamItemsForMedia(
    searchQuery: string,
    tagIds?: number[],
    languageCodes?: string[],
    apiOffset: number = 0,
    minVideoCount: number = API_PAGE_SIZE,
  ): Promise<ClippingStudioMamMediaPage> {
    const tags = tagIds ?? [];
    const languages = languageCodes ?? [];
    const videos: ClippingStudioMamItem[] = [];
    let offset = apiOffset;
    let exhausted = false;

    while (videos.length < minVideoCount && !exhausted) {
      const mediaItems = await fetchMediaPage(
        this.mediaRepository,
        searchQuery,
        offset,
        tags,
        languages,
      );

      offset += mediaItems.length;
      if (mediaItems.length === 0 || mediaItems.length < API_PAGE_SIZE) {
        exhausted = true;
      }

      for (const item of mediaItems) {
        if (!isVideoMediaItem(item.contentMediaType)) {
          continue;
        }
        videos.push(mapMediaItemToMamItem(item, "video"));
      }
    }

    return {
      items: videos,
      nextOffset: offset,
      exhausted,
    };
  }
}
